import { ChannelNameWithParams, CreateMixin, Cable, Channel } from 'actioncable';
import { useCallback, useEffect, useRef, useState } from 'react';

export const useChannel = (actionCable: Cable) => {
  const [connected, setConnected] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const channelRef = useRef<Channel | null>();

  const subscribe = useCallback(
    (data: string | ChannelNameWithParams, callbacks: CreateMixin) => {
      const channel = actionCable.subscriptions.create(data, {
        received: (data) => {
          if (callbacks.received) {
            callbacks.received(data);
          }
        },
        initialized: () => {
          setSubscribed(true);
          if (callbacks.initialized) {
            callbacks.initialized();
          }
        },
        connected: () => {
          setConnected(true);
          if (callbacks.connected) {
            callbacks.connected();
          }
        },
        disconnected: () => {
          setConnected(false);
          if (callbacks.disconnected) {
            callbacks.disconnected();
          }
        },
      });
      channelRef.current = channel;
    },
    [actionCable.subscriptions]
  );

  const unsubscribe = useCallback(() => {
    setSubscribed(false);

    if (channelRef.current) {
      channelRef.current.unsubscribe();
      channelRef.current = null;
    }
  }, []);

  const perform = (action: string, payload: object) => {
    if (subscribed && !connected) {
      throw 'ActionCable: not connected';
    }
    if (!subscribed) {
      throw 'ActionCable: not subscribed';
    }
    try {
      channelRef.current?.perform(action, payload);
    } catch {
      throw 'ActionCable: Unknown error';
    }
  };

  const send = ({ action, payload }: { action: string; payload: object }) => {
    perform(action, payload);
  };

  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, [unsubscribe]);

  return {
    subscribe,
    unsubscribe,
    send,
  };
};
